/**
 * Day view calendar component.
 * Mirrors the week view layout as a single-column 24-hour time grid, with
 * gutter labels honoring the org time-format setting.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getHourInTimezone } from '@/lib/scheduling/timezone';
import { todayIn, useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import {
  allDayEventDateRange,
  calendarDayBounds,
  calendarDayKey,
} from '../utils/calendarDays';
import { isEventPast } from '../utils/calendarUtils';
import { hourLabelsFor } from '../utils/dateUtils';
import type { CalendarEvent } from '../utils/eventMapper';
import {
  MAX_VISIBLE_ALL_DAY_LANES,
  MoreEventsPopover,
} from './DayEventsPopover';
import { MonthEventChip } from './MonthEventChip';
import { WeekDayColumn } from './WeekDayColumn';

interface DayViewProps {
  /** Events to display on the calendar */
  events: CalendarEvent[];
  /** The calendar day being displayed (profile-tz midnight) */
  date: Date;
  /** Starting hour for the grid (0-23), default 0 (midnight) */
  startHour?: number;
  /** Ending hour for the grid (0-23), default 24 (midnight) */
  endHour?: number;
  /** Handler for clicking on an event */
  onEventClick?: (event: CalendarEvent) => void;
  /** ID of the currently selected event (for highlighting) */
  selectedEventId?: string;
  /**
   * Handler for clicking an empty slot. Timed-grid clicks pass an ISO datetime
   * with `allDay=false`; an all-day click passes this day's `YYYY-MM-DD` with
   * `allDay=true`. When omitted, slots are not interactive.
   */
  onSlotSelect?: (value: string, allDay: boolean) => void;
  /**
   * IANA timezone (the user's profile timezone) this day is resolved in —
   * which events belong to it, where in the grid they sit, today-highlight
   * and the now-line. See utils/calendarDays.ts.
   */
  timezone: string;
}

/**
 * DayView - Single day calendar view component.
 *
 * Mirrors the WeekView layout as a single column:
 * - All-day events in a header section
 * - 24-hour time grid with 60px per hour, gutter labels per org time format
 * - Current time indicator on today
 */
export function DayView({
  events,
  date,
  startHour = 0,
  endHour = 24,
  onEventClick,
  selectedEventId,
  onSlotSelect,
  timezone,
}: DayViewProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { timeFormat } = useOrgTimeFormat();

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to current hour (or 7am) on mount
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    // The current hour on the user's clock, not the laptop's.
    const nowHour = getHourInTimezone(new Date(), timezone);
    const targetHour = Math.max(0, Math.min(nowHour - 1, 7));
    const pixelsPerHour = 60; // matches WeekView gridHeight calculation
    container.scrollTop = (targetHour - startHour) * pixelsPerHour;
  }, [startHour, timezone]);

  // Separate all-day events from timed events, keeping only those that fall
  // on the displayed calendar day IN THE PROFILE TIMEZONE.
  //
  // All-day events are compared as calendar dates (an all-day event is the
  // days it names, in any zone); timed events are compared as instants
  // against this day's real boundaries — an event overlaps when
  // [eventStart, eventEnd) intersects [dayStart, dayEnd). Both correctly show
  // a multi-day event on every day it spans. Malformed timestamps are
  // silently skipped.
  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay: CalendarEvent[] = [];
    const timed: CalendarEvent[] = [];

    const dayYmd = calendarDayKey(date, timezone);
    const { dayStart, dayEnd } = calendarDayBounds(date, timezone);

    for (const event of events) {
      if (event.is_all_day) {
        const range = allDayEventDateRange(event);
        if (range && range.startYmd <= dayYmd && dayYmd <= range.endYmd) {
          allDay.push(event);
        }
        continue;
      }
      const eventStart = new Date(event.start_time);
      const eventEnd = new Date(event.end_time);
      if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) {
        continue;
      }
      if (eventStart < dayEnd && eventEnd > dayStart) {
        timed.push(event);
      }
    }

    return { allDayEvents: allDay, timedEvents: timed };
  }, [events, date, timezone]);

  const hasAllDayOverflow = allDayEvents.length > MAX_VISIBLE_ALL_DAY_LANES;
  const visibleAllDayEvents = hasAllDayOverflow
    ? allDayEvents.slice(0, MAX_VISIBLE_ALL_DAY_LANES)
    : allDayEvents;
  const hiddenAllDayCount = allDayEvents.length - visibleAllDayEvents.length;

  const isToday =
    calendarDayKey(date, timezone) === todayIn(timezone, currentTime);

  // Click on empty space in the all-day section → date-only task on this
  // day. Chips stopPropagation, so clicking an existing all-day event opens it.
  const handleAllDayClick = useCallback(() => {
    onSlotSelect?.(calendarDayKey(date, timezone), true);
  }, [onSlotSelect, date, timezone]);

  // Calculate grid height based on visible hours — matches WeekView (60px per hour)
  const totalHours = endHour - startHour;
  const gridHeight = totalHours * 60;

  // Filter hour labels to only show visible range, honoring the org time format
  const visibleHours = useMemo(() => {
    const hourLabels = hourLabelsFor(timeFormat);
    const hours: { label: string; hour: number }[] = [];
    for (let h = startHour; h < endHour; h++) {
      hours.push({ label: hourLabels[h], hour: h });
    }
    return hours;
  }, [startHour, endHour, timeFormat]);

  return (
    <div className='flex flex-col h-full'>
      <div ref={scrollContainerRef} className='flex-1 overflow-auto'>
        {/* All-day events row. Always rendered (with a min-height empty area)
            so the day is a click target for creating a date-only task. */}
        <div className='sticky top-0 z-10 bg-primary'>
          <div className='flex border-b border-secondary'>
            <div className='w-16 flex-shrink-0 py-1 pr-2 text-right'>
              <span className='text-[11px] text-tertiary'>All day</span>
            </div>
            <div
              onClick={handleAllDayClick}
              className={cx(
                'flex-1 py-1 px-0.5 space-y-0.5 min-h-[30px]',
                onSlotSelect && 'cursor-pointer'
              )}
            >
              {visibleAllDayEvents.map(event => (
                <MonthEventChip
                  key={event.id}
                  timezone={timezone}
                  event={event}
                  onClick={onEventClick}
                  isMultiDay
                  isSelected={event.id === selectedEventId}
                  isPast={isEventPast(event)}
                />
              ))}
              {hasAllDayOverflow && (
                <MoreEventsPopover
                  date={date}
                  timezone={timezone}
                  events={allDayEvents}
                  count={hiddenAllDayCount}
                  onEventClick={onEventClick}
                />
              )}
            </div>
          </div>
        </div>

        {/* Time grid with events — mirrors WeekView layout */}
        <div className='flex' style={{ height: `${gridHeight}px` }}>
          {/* Time gutter (hour labels) */}
          <div className='w-16 flex-shrink-0 relative'>
            {visibleHours.map(({ label, hour }, index) => {
              const topPercent = (index / totalHours) * 100;
              return (
                <div
                  key={hour}
                  className='absolute right-2 -mt-2 text-right'
                  style={{ top: `${topPercent}%` }}
                >
                  <span className='text-[11px] text-tertiary font-medium'>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Single day column */}
          <div className='flex-1 relative'>
            {/* Hour lines (horizontal) */}
            {visibleHours.map(({ hour }, index) => {
              const topPercent = (index / totalHours) * 100;
              return (
                <div
                  key={hour}
                  className='absolute left-0 right-0 border-t border-secondary pointer-events-none'
                  style={{ top: `${topPercent}%` }}
                />
              );
            })}

            {/* Day column with events */}
            <WeekDayColumn
              date={date}
              events={timedEvents}
              startHour={startHour}
              endHour={endHour}
              isToday={isToday}
              currentTime={isToday ? currentTime : undefined}
              timezone={timezone}
              onEventClick={onEventClick}
              selectedEventId={selectedEventId}
              onSlotSelect={onSlotSelect}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
